import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import appendQuery from 'append-query';
import 'url-search-params-polyfill';

import LoginContainer from 'platform/user/authentication/components/LoginContainer';
import { isLoggedIn } from 'platform/user/selectors';
import { externalApplicationsConfig } from 'platform/user/authentication/usip-config';
import { OAuthEnabledApplications } from 'platform/user/authentication/config/constants';
import {
  CSP_IDS,
  SIGNUP_TYPES,
  EXTERNAL_APPS,
  AUTHN_SETTINGS,
} from 'platform/user/authentication/constants';
import { signupOrVerify } from 'platform/user/authentication/utilities';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { signInAppCSS } from '../constants';

const SIGNUP_TYPE_TO_POLICY = Object.fromEntries(
  Object.entries(SIGNUP_TYPES).map(([policy, type]) => [type, policy]),
);

// Maps MHV `to` deep-link values to their VA.gov equivalents.
// Falls back to /health-care/manage-health for unmapped values.
const MHV_TO_VA_GOV_ROUTES = {
  home: '/health-care/manage-health',
  appointments: '/my-health/appointments',
  'secure-messaging': '/my-health/secure-messages',
  'web/myhealthevet/refill-prescriptions': '/my-health/medications/refill',
  'labs-tests': '/my-health/medical-records/labs-and-tests',
  'download-my-data': '/my-health/medical-records/download',
};

export function UnifiedSigninPage({ router, location }) {
  const isAuthenticated = useSelector(state => isLoggedIn(state));
  const { query } = location;
  const externalApplication = query.application;
  const clientId = query.client_id;
  const { TOGGLE_NAMES, useToggleValue, useToggleLoadingValue } =
    useFeatureToggle();
  const isLoadingToggles = useToggleLoadingValue();
  const clearSigninEnabled = useToggleValue(TOGGLE_NAMES.clearSigninEnabled);

  // CLEAR signup is gated by `clear_signin_enabled`; when off, fall back to USiP
  const requestedSignupPolicy = SIGNUP_TYPE_TO_POLICY[query.type];
  const isClearSignup = requestedSignupPolicy === CSP_IDS.CLEAR;
  const isWaitingOnToggles = isClearSignup && isLoadingToggles;
  const signupPolicy =
    isClearSignup && !clearSigninEnabled ? undefined : requestedSignupPolicy;

  const { OAuthEnabled } =
    externalApplicationsConfig[externalApplication] ??
    externalApplicationsConfig.default;
  // Intercept MHV entry point: strip params, use OAuth, land on VA.gov MyHealth page
  // TODO: remove environment gate once validated in production
  useEffect(() => {
    if (externalApplication === EXTERNAL_APPS.MHV) {
      const returnPath =
        MHV_TO_VA_GOV_ROUTES[query.to] ?? '/health-care/manage-health';
      sessionStorage.setItem(
        AUTHN_SETTINGS.RETURN_URL,
        `${window.location.origin}${returnPath}`,
      );
      router.replace('/?oauth=true');
    }
  }, []);

  // Check if authenticated and redirect if necessary
  useEffect(() => {
    if (isAuthenticated) {
      window.location = '/';
    }

    document.title = `Sign in or create an account | Veterans Affairs`;
  }, []);

  // Check if page is being used for signup
  useEffect(() => {
    if (isAuthenticated || isWaitingOnToggles || !signupPolicy) {
      return;
    }

    signupOrVerify({ policy: signupPolicy, isSignup: true });
  }, [isWaitingOnToggles]);

  // Immediately check if OAuthEnabled
  useEffect(() => {
    if (
      isAuthenticated ||
      isWaitingOnToggles ||
      signupPolicy ||
      externalApplication === EXTERNAL_APPS.MHV ||
      (query?.oauth === 'true' &&
        OAuthEnabledApplications.includes(externalApplication))
    ) {
      return;
    }

    router.push(
      appendQuery('', { ...query, oauth: OAuthEnabled }, { removeNull: true }),
    );
  }, [isWaitingOnToggles]);

  if (isWaitingOnToggles || signupPolicy) {
    return null;
  }

  return (
    <>
      <style>{signInAppCSS}</style>
      <LoginContainer
        isUnifiedSignIn
        externalApplication={externalApplication}
        clientId={clientId}
      />
    </>
  );
}

UnifiedSigninPage.propTypes = {
  location: PropTypes.object,
  router: PropTypes.object,
};

export default UnifiedSigninPage;
